import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Alert, Box, Button, Icon, Popover, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { TPEReactTable } from 'src/components/shared/TPEReactTable';
import { renderExpressionParts } from 'src/components/shared/FormulaExpressionFunctionComponents';
import ErrorUtils from 'src/utils/ErrorUtils';
import CONSTANTS from 'src/utils/constants';
import {
    CommonBalancePoolColumn,
    CommonBalancePoolColumnDataType,
} from 'src/models/multidimensional-allocation/CommonBalancePool';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import { toExpressionParts } from './cbpFormula';
import 'src/assets/styles/react-table.scss';

// A CBP can reach ~10k rows (LLD §7.5); the materialized results page in a bounded chunk at a time,
// with a "Load more" driven by the response's nextToken.
const ROWS_PAGE_SIZE = 50;

/** UI label for a CBP grain, mirroring AddCbpInputModal (COST_CENTER -> "Cost Center", LEGAL_ENTITY -> Company). */
const grainUiLabel = (grain?: string): string =>
    (grain
        ? (CONSTANTS.COA_SEGMENT_MAPPING as Record<string, { UI: string }>)[grain === 'LEGAL_ENTITY' ? 'COMPANY' : grain]?.UI ?? grain
        : 'Grain');

/** "Cost Center" -> "cost centers" for the count caption + totals label. */
const grainPluralLower = (grain?: string): string => {
    const label = grainUiLabel(grain).toLowerCase();
    return label.endsWith('y') ? `${label.slice(0, -1)}ies` : `${label}s`;
};

/** Storage is always numeric; PERCENTAGE renders ×100 + "%". A missing/NaN value renders as an en dash. */
const formatCbpValue = (value: number | undefined, dataType?: CommonBalancePoolColumnDataType): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '–';
    if (dataType === 'PERCENTAGE') {
        return `${(value * 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    }
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

/**
 * The source sub-label under a column header (per the LLD's sourced/derived lineage): a CALC column is
 * "Calculated"; an INPUT column shows its source object ("CDT | <table>" / "MDA | <sheet>"). Derived
 * from the column's own kind/sourceType/sourceRef — nothing is hard-coded.
 */
const columnSourceLabel = (col: CommonBalancePoolColumn): string => {
    if (col.kind === 'CALC') return 'Calculated';
    if (col.sourceType === 'SEGMENT_CDT') {
        const src = col.sourceRef?.cdtTableName ?? col.sourceRef?.cdtTableId;
        return src ? `CDT | ${src}` : 'CDT';
    }
    if (col.sourceType === 'MDA_OUTPUT') {
        const src = col.sourceRef?.mdaSheetName ?? col.sourceRef?.mdaSheetId;
        return src ? `MDA | ${src}` : 'MDA';
    }
    return '';
};

/** A two-line column header: the display name over a secondary source label (+ optional info popover). */
const columnHeader = (title: string, subLabel: string, info?: React.ReactNode) => (
    <div className="cell-text-non-sortable">
        <div>{title}</div>
        <Box fontSize="body-s" color="text-body-secondary">
            {subLabel}
            {info && (
                <Popover dismissButton={false} position="top" size="medium" triggerType="custom" content={info}>
                    {' '}
                    <Icon name="status-info" size="small" variant="link" />
                </Popover>
            )}
        </Box>
    </div>
);

// A grid row: the grain value plus its derived (CALC) column values.
type CbpGridRow = Record<string, any> & { grainValue: string };

/**
 * Step 3 — Validate & preview. Shows the pool's **materialized results for the period selected in the
 * top status bar**: one row per grain value, a column per derived (CALC) column (formatted by its data
 * type). Data comes from a single paginated read — GetCommonBalancePoolRows(poolId, period) — re-fetched
 * from the first page whenever the period changes, so selecting a past (closed) period shows that period's
 * immutable results. Download exports the period's rows to a spreadsheet via a presigned URL
 * (DownloadCommonBalancePool); Validate is still a placeholder, filled in a later change. The
 * grain-description column and a pool-wide totals footer are intentionally deferred until the backend
 * supplies them.
 */
export default function CbpResultsSection() {
    const { cbpBuilderState, cbpBuilderDispatch, services, poolId } = useContext(CommonBalancePoolBuilderContext);
    // The Step-3 preview slice lives in the builder reducer (per the package convention), not as
    // component-local state; this section reads it from context and dispatches the RESULTS_* actions.
    const {
        pool,
        period,
        resultsRows: rows,
        resultsNextToken: nextToken,
        resultsTotalRowCount: totalRowCount,
        resultsLoading: loading,
        resultsError: error,
        resultsDownloading: downloading,
        resultsDownloadError: downloadError,
    } = cbpBuilderState;
    const periodValue = period?.value;
    const periodLabel = period?.label ?? periodValue;

    // Only the most recent request's result is applied — guards a fast period switch / overlapping loads.
    // A mutable ref (transient async orchestration), not reducer state.
    const reqIdRef = useRef(0);

    // Product decision (2026-09): the preview shows only DERIVED (CALC) columns — input columns are NOT
    // shown — alongside the grain value. A materialized ROW# still carries a value for every column, so we
    // simply render the CALC subset. Headers come from the definition (period-independent); only the values
    // are per-period. (Grain description is deferred until the row carries it.)
    const columns: CommonBalancePoolColumn[] = useMemo(
        () => (pool?.columns ?? [])
            .filter((col) => col.kind === 'CALC')
            .sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0)),
        [pool],
    );

    // The single per-period read for Step 3 (no separate "light preview"): one paginated
    // GetCommonBalancePoolRows keyed on the selected period. `append` adds the next page; otherwise it
    // replaces (first page). Runs inside try/catch so a synchronous failure becomes the error state.
    const fetchRows = useCallback(async (token: string | undefined, append: boolean) => {
        if (!periodValue) return;
        const reqId = reqIdRef.current + 1;
        reqIdRef.current = reqId;
        cbpBuilderDispatch(ACTIONS.RESULTS_LOADING.withPayload(undefined));
        try {
            const resp = await services.commonBalancePoolService.getCommonBalancePoolRows(poolId, periodValue, ROWS_PAGE_SIZE, token);
            if (reqId !== reqIdRef.current) return; // a newer request superseded this one
            cbpBuilderDispatch(ACTIONS.RESULTS_LOADED.withPayload({
                rows: resp.rows ?? [],
                nextToken: resp.nextToken,
                totalRowCount: resp.totalRowCount,
                append,
            }));
        } catch (ex) {
            if (reqId === reqIdRef.current) cbpBuilderDispatch(ACTIONS.RESULTS_ERROR.withPayload(ErrorUtils.getMessage(ex)));
        }
    }, [periodValue, poolId, services, cbpBuilderDispatch]);

    // (Re)load the first page whenever the selected period (or pool) changes; clear stale rows first so a
    // period switch never shows the previous period's data.
    useEffect(() => {
        cbpBuilderDispatch(ACTIONS.RESULTS_RESET.withPayload(undefined));
        if (periodValue) fetchRows(undefined, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodValue, poolId]);

    const reloadFirstPage = useCallback(() => fetchRows(undefined, false), [fetchRows]);
    const loadMore = useCallback(() => fetchRows(nextToken, true), [fetchRows, nextToken]);

    // Validate / Download — wired now; the implementations (backend validation + export) land later.
    const handleValidate = useCallback(() => {
        // TODO(cbp-step3): call validateCommonBalancePool(poolId, period) and surface the findings.
    }, []);
    // Download: fetch a presigned URL for the period's materialised rows and open it. The URL is one-shot,
    // so we navigate to it rather than storing it; a failure surfaces as a dismissible error.
    const handleDownload = useCallback(async () => {
        if (!periodValue) return;
        cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_START.withPayload(undefined));
        try {
            const resp = await services.commonBalancePoolService.downloadCommonBalancePool(poolId, periodValue);
            if (resp?.downloadUrl) window.open(resp.downloadUrl, '_blank', 'noopener,noreferrer');
            cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_END.withPayload(undefined));
        } catch (ex) {
            cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_END.withPayload(ErrorUtils.getMessage(ex)));
        }
    }, [periodValue, poolId, services, cbpBuilderDispatch]);

    const hasRows = rows.length > 0;
    const grainPlural = grainPluralLower(pool?.grain);

    // GetCommonBalancePoolRows deliberately omits totalRowCount (a page-size "total" would grow as the user
    // pages — "50 of 50", then "100 of 100"). So show the "of N" clause only when the backend actually
    // supplies a total; otherwise report just the loaded count.
    const countCaption = totalRowCount != null
        ? `Showing ${rows.length.toLocaleString('en-US')} of ${totalRowCount.toLocaleString('en-US')} ${grainPlural} for ${periodLabel}.`
        : `Showing ${rows.length.toLocaleString('en-US')} ${grainPlural} for ${periodLabel}.`;

    // One row per grain value; its derived (CALC) column values are spread in. The grain description column
    // and the totals footer are intentionally omitted here — both need backend work not yet shipped (the row
    // does not carry a description yet, and a pool-wide total must be computed server-side, not summed on the
    // client), so they land in a later change.
    const gridData: CbpGridRow[] = useMemo(
        () => rows.map((r) => ({ grainValue: r.grainValue, ...r.values })),
        [rows],
    );

    // columnId -> display name over ALL columns (inputs + calc), so a CALC column's info popover can
    // render its formula with the referenced inputs' names, not raw column ids.
    const nameById = useMemo(
        () => Object.fromEntries((pool?.columns ?? []).map((c) => [c.columnId, c.displayName || c.columnId])),
        [pool],
    );

    // Headers from the pool DEFINITION + the grain (nothing hard-coded): the grain value, then every derived
    // (CALC) pool column in order, each with a source sub-label.
    const columnDefinitions = useMemo(() => {
        const grainLabel = grainUiLabel(pool?.grain);
        return [
            {
                accessor: 'grainValue',
                Header: () => columnHeader(grainLabel, 'FRAMES'),
                Cell: ({ value }: any) => <div className="cell-text">{value}</div>,
            },
            ...columns.map((col) => ({
                accessor: col.columnId,
                Header: () => columnHeader(
                    col.displayName || col.columnId,
                    columnSourceLabel(col),
                    col.kind === 'CALC' && col.formula ? renderExpressionParts(toExpressionParts(col.formula, nameById)) : undefined,
                ),
                Cell: ({ value }: any) => <div className="cell-text">{formatCbpValue(value, col.dataType)}</div>,
            })),
        ];
    }, [columns, pool?.grain, nameById]);

    return (
        <SpaceBetween direction="vertical" size="m">
            <Box color="text-body-secondary">
                Preview the materialized CBP; run validation before submitting. Blocking issues prevent downstream
                consumption; warnings are surfaced for review.
            </Box>

            {/* "Materialized preview" heading on the left, the Validate / Download actions on the right. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box variant="h4" padding={{ top: 'n' }}>Materialized preview</Box>
                <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={handleValidate}>Validate</Button>
                    <Button onClick={handleDownload} loading={downloading} disabled={downloading || !hasRows}>
                        Download
                    </Button>
                </SpaceBetween>
            </div>

            {/* Download failures surface here (the button is disabled until there are rows to export). */}
            {downloadError && (
                <Alert type="error" dismissible header="Could not download"
                    onDismiss={() => cbpBuilderDispatch(ACTIONS.RESULTS_DOWNLOAD_END.withPayload(undefined))}>
                    {downloadError}
                </Alert>
            )}

            {/* No columns configured yet — nothing to show. */}
            {columns.length === 0 && (
                <StatusIndicator type="info">
                    Configure inputs (Step 1) and calculated columns (Step 2) to preview the table.
                </StatusIndicator>
            )}

            {/* The grid renders whenever the pool has columns: HEADERS come from the definition (visible
                immediately); the VALUES are the selected period's materialized rows, filled in by the
                paginated GetCommonBalancePoolRows read. A status line explains the state. */}
            {columns.length > 0 && (
                <>
                    {!periodValue && (
                        <StatusIndicator type="info">Select a period (top of the page) to load results.</StatusIndicator>
                    )}
                    {periodValue && loading && !hasRows && (
                        <Box color="text-body-secondary" fontSize="body-s">Loading results for {periodLabel}…</Box>
                    )}
                    {periodValue && !loading && error && (
                        <Alert type="error" dismissible header="Could not load results" onDismiss={() => cbpBuilderDispatch(ACTIONS.RESULTS_ERROR.withPayload(undefined))}>
                            <SpaceBetween direction="vertical" size="xs">
                                <Box>{error}</Box>
                                <Box><Button iconName="refresh" onClick={reloadFirstPage}>Retry</Button></Box>
                            </SpaceBetween>
                        </Alert>
                    )}
                    {periodValue && !error && hasRows && (
                        <Box color="text-body-secondary" fontSize="body-s">{countCaption}</Box>
                    )}
                    {periodValue && !loading && !error && !hasRows && (
                        <Box color="text-body-secondary" fontSize="body-s">
                            No materialized rows for {periodLabel}. Execute this pool for the period to populate results.
                        </Box>
                    )}

                    <TPEReactTable {...{
                        // Values only when the period returned rows; otherwise just the headers + empty message.
                        data: hasRows ? gridData : [],
                        columnDefinitions,
                        // Real auto-layout table (see react-table.scss) so the variable column set lays out
                        // horizontally and the headers stay visible even with an empty body.
                        className: 'nonGridTable cbpResultsGrid',
                        sortable: false,
                        key: 'cbpResultsGrid',
                        emptyMessage: periodValue ? 'Results appear here once the period is executed.' : 'Select a period to load results.',
                    }} />

                    {nextToken && (
                        <Box textAlign="center">
                            <Button onClick={loadMore} loading={loading} disabled={loading}>Load more</Button>
                        </Box>
                    )}
                </>
            )}
        </SpaceBetween>
    );
}
